import {useEffect, useRef} from "react";
import * as h3 from "h3-js";
import L from "leaflet";

if (typeof window !== "undefined" && !window.openInfoWindows) {
    window.openInfoWindows = [];
}

export const Hexagon = ({
    hexagon,
    hoverHexagon,
    color,
    opacity,
    isSelected,
    isPreview,
    map,
    setHoverHexagon,
    zIndex,
    showTooltip = false,
    tooltip = null
}) => {
    const polygonRef = useRef(null);
    const popupRef = useRef(null);

    function createTooltipContent(tooltip) {
        if (!tooltip) return "";
        return `
            <style>${leafletTooltipStyles}</style>
            <div class="custom-tooltip-leaflet">
                <div style="display: flex; justify-content: space-between; align-items: center; background: #f5f7fa; color: #222; font-size: 16px; font-weight: 700; padding: 14px 18px 8px 18px; border-bottom: 1px solid #ececec; letter-spacing: 0.01em;">
                    <span>${tooltip.title || ""}</span>
                    <span class="tooltip-close-leaflet" style="cursor:pointer; font-size:20px; padding-left:12px;">×</span>
                </div>
                ${tooltip.content || ""}
            </div>
        `;
    }

    useEffect(() => {
        if (showTooltip && !tooltip) {
            return;
        }

        if (!map || !hexagon) {
            return;
        }

        const vertices = h3.cellToBoundary(hexagon);
        const latLngs = vertices.map(([lat, lng]) => [lat, lng]);
        const isHovered = hoverHexagon === hexagon;

        const polygon = L.polygon(latLngs, {
            fillColor: color || "#FF0000",
            fillOpacity: isPreview ? 0.15 : isHovered ? 0.5 : opacity || 0.3,
            color: isPreview ? "#FFFF00" : color || "#FF0000",
            weight: 0.3,
            opacity: isPreview ? 0.5 : 0.8,
            interactive: tooltip ? true : false
        });

        polygon.addTo(map);
        polygonRef.current = polygon;

        // Set z-index using pane if needed
        if (zIndex) {
            polygon.setStyle({pane: "overlayPane"});
        }

        if (tooltip) {
            const popup = L.popup({
                closeButton: false,
                className: "custom-leaflet-popup"
            }).setContent(createTooltipContent(tooltip));

            polygon.bindPopup(popup);
            popupRef.current = popup;

            polygon.on("click", () => {
                if (window.openInfoWindows && Array.isArray(window.openInfoWindows)) {
                    window.openInfoWindows.forEach(p => {
                        if (p && p.remove) p.remove();
                    });
                    window.openInfoWindows = [];
                }
                polygon.openPopup();
                window.openInfoWindows.push(popup);

                // Add close button handler after popup is opened
                setTimeout(() => {
                    const closeBtn = document.querySelector(".tooltip-close-leaflet");
                    if (closeBtn) {
                        closeBtn.onclick = () => {
                            polygon.closePopup();
                        };
                    }
                }, 100);
            });

            polygon.on("mouseover", () => {
                setHoverHexagon(hexagon);
            });

            polygon.on("mouseout", () => {
                setHoverHexagon(null);
            });
        }

        // Close popups on map zoom/drag
        const closePopups = () => {
            setHoverHexagon(null);
            if (popupRef.current) {
                polygon.closePopup();
            }
        };

        map.on("zoomstart", closePopups);
        map.on("dragstart", closePopups);

        return () => {
            map.off("zoomstart", closePopups);
            map.off("dragstart", closePopups);

            if (popupRef.current) {
                polygon.closePopup();
            }

            if (polygon && map) {
                map.removeLayer(polygon);
            }
        };
    }, [
        hexagon,
        color,
        opacity,
        isSelected,
        isPreview,
        map,
        zIndex,
        showTooltip,
        tooltip,
        hoverHexagon,
        JSON.stringify(tooltip)
    ]);

    return null;
};

const leafletTooltipStyles = `
    .custom-tooltip-leaflet {
        background: #fff;
        border-radius: 14px;
        box-shadow: 0 8px 32px rgba(60,60,60,0.18);
        max-width: 320px;
        min-width: 200px;
        padding: 0;
        overflow: hidden;
    }
    .tooltip-content {
        font-size: 14px;
        color: #444;
        padding: 10px 18px 18px 18px;
        line-height: 1.6;
        background: #fff;
    }
    .custom-leaflet-popup .leaflet-popup-content-wrapper {
        background: transparent;
        padding: 0;
        box-shadow: none;
    }
    .custom-leaflet-popup .leaflet-popup-content {
        margin: 0;
        width: auto !important;
    }
    .custom-leaflet-popup .leaflet-popup-tip-container {
        display: none;
    }
`;
